import { useIntl } from 'react-intl';

import { APOLLO_CLIENTS } from '@/graphql/client';
import type { OrderAppError } from '@/graphql/orderApp/api';
import {
  useCheckoutVoucherAddMutation,
  useCheckoutVoucherDeleteMutation,
} from '@/graphql/orderApp/api';

export const useVoucher = (isCheckoutStepTwo = true) => {
  const [
    checkoutVoucherDeleteMutation,
    { loading: voucherDeleteMutationLoading },
  ] = useCheckoutVoucherDeleteMutation({
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  const [checkoutVoucherAddMutation, { loading: voucherAddMutationLoading }] =
    useCheckoutVoucherAddMutation({
      context: {
        client: APOLLO_CLIENTS.orderApp,
        disableErrorNotification: true,
      },
    });

  const { formatMessage } = useIntl();

  const getErrorMessage = (error: OrderAppError, code?: string) => {
    const _defaultErrorMessage = formatMessage({
      id: 'checkout.shoppingBag.voucher.genericError',
    });

    const errorMessages: Record<string, string> = {
      'voucher.other_voucher_applied':
        'checkout.shoppingBag.voucher.otherVoucherApplied',
      'voucher.used_with_strap_pack':
        'checkout.shoppingBag.voucher.usedWithStrapPack',
      'voucher.used_without_watch': 'checkout.shoppingBag.voucher.watchMissing',
      'voucher.used_without_strap': 'checkout.shoppingBag.voucher.strapMissing',
      'voucher.discount_exceeds_max_value':
        'checkout.shoppingBag.voucher.discountExceedsMaxValue',
      voucher_applicable_if_no_other_discount:
        'checkout.shoppingBag.voucher.applicableIfNoOtherDiscount',
      'voucher.used_with_duplicated_straps':
        'checkout.shoppingBag.voucher.usedWithDuplicatedStraps',
      'voucher.used_with_duplicated_buckles':
        'checkout.shoppingBag.voucher.usedWithDuplicatedBuckles',
      'voucher.is_not_active': 'checkout.shoppingBag.voucher.isNotActive',
      'voucher.applied_before': 'checkout.shoppingBag.voucher.alreadyApplied',
      'voucher.is_expired': 'checkout.shoppingBag.voucher.isExpired',
      'voucher.code_is_empty': 'checkout.shoppingBag.voucher.codeIsEmpty',
      'voucher.fake_voucher_applied_without_email': isCheckoutStepTwo
        ? 'checkout.shoppingBag.voucher.fakeVoucherAppliedWithoutEmail'
        : 'checkout.shoppingBag.voucher.fakeVoucherAppliedWithoutEmailInWrongStep',
    };

    if (error.code in errorMessages) {
      return formatMessage(
        { id: errorMessages[error.code] },
        { voucher: code },
      );
    }

    if (error.code === 'voucher.applied_voucher_failed') {
      try {
        const errorContext = error?.context
          ? JSON.parse(error?.context)
          : undefined;
        return errorContext?.errors?.[0].message ?? error.message;
      } catch (e) {
        return _defaultErrorMessage;
      }
    }

    return _defaultErrorMessage;
  };

  const deleteVoucher = async (id: string) => {
    if (voucherDeleteMutationLoading) return;
    const { data } = await checkoutVoucherDeleteMutation({
      variables: { id },
    });

    if (data?.checkoutVoucherDelete.errors?.length) {
      const _error = data?.checkoutVoucherDelete.errors?.[0];
      const _formattedMsg = getErrorMessage(_error);
      return _formattedMsg;
    }

    return '';
  };

  const addVoucher = async (id: string, code: string) => {
    if (voucherAddMutationLoading) return;
    const { data } = await checkoutVoucherAddMutation({
      variables: { id, code },
    });

    if (data?.checkoutVoucherAdd.errors?.length) {
      const _error = data.checkoutVoucherAdd.errors?.[0];
      const _formattedMsg = getErrorMessage(_error, code);
      return _formattedMsg;
    }
    return '';
  };

  return {
    addVoucher,
    deleteVoucher,
  };
};
